import React from 'react';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import {useTranslation} from 'react-i18next';
import {Panel} from 'reactflow';

const CONTROL_SIZE = 36;

interface EditorToolbarPanelProps {
    onAdd: (anchor: HTMLElement) => void;
    /** True while the node picker hangs off this button. */
    addActive?: boolean;
    onUndo: () => void;
    onRedo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    /** What Undo would revert, when the change was labelled (the agent labels its batches). */
    undoLabel?: string;
    /** What Redo would reapply, when the change was labelled. */
    redoLabel?: string;
}

const EditorToolbarPanel: React.FC<EditorToolbarPanelProps> = ({
    onAdd,
    addActive,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    undoLabel,
    redoLabel
}) => {
    const {t} = useTranslation();
    // The tooltip names the change; the accessible name stays the plain action.
    const undoTitle = undoLabel ? `${t('Undo')} · ${undoLabel}` : t('Undo');
    const redoTitle = redoLabel ? `${t('Redo')} · ${redoLabel}` : t('Redo');

    return (
        <Panel position={'top-left'}>
            <Paper
                variant={'outlined'}
                sx={{
                    overflow: 'hidden',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    borderColor: 'divider'
                }}
            >
                <Stack direction={'row'} alignItems={'center'}>
                    <Button
                        size={'small'}
                        variant={addActive ? 'contained' : 'text'}
                        disableElevation
                        startIcon={<AddIcon/>}
                        onClick={event => onAdd(event.currentTarget)}
                        sx={{borderRadius: 0, height: CONTROL_SIZE, minWidth: 0, px: 1.25}}
                    >
                        {t('Add')}
                    </Button>

                    <Divider orientation={'vertical'} flexItem sx={{mx: 0.5, my: 0.75}}/>

                    <Tooltip title={undoTitle}>
                        <span>
                            <IconButton
                                size={'small'}
                                aria-label={t('Undo')}
                                disabled={!canUndo}
                                onClick={onUndo}
                                sx={{width: CONTROL_SIZE, height: CONTROL_SIZE}}
                            >
                                <UndoIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={redoTitle}>
                        <span>
                            <IconButton
                                size={'small'}
                                aria-label={t('Redo')}
                                disabled={!canRedo}
                                onClick={onRedo}
                                sx={{width: CONTROL_SIZE, height: CONTROL_SIZE}}
                            >
                                <RedoIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Paper>
        </Panel>
    );
};

export default EditorToolbarPanel;
